import { useDispatch, useSelector } from "react-redux";

import Avatar from "@/design/components/Avatar";
import { setSystemSettingsPage } from "@/store/reducers/app";
import { getVersion } from "@/utils/utility";
import Valkyrie, {
  viArrowLeft,
  viChartPie,
  viCity,
  viGauge,
  viKeyboard,
  viLock,
  viSippy,
  viSwatchbook,
} from "@sippy-platform/valkyrie";

import {
  AppBar,
  Box,
  Dialog,
  Divider,
  IconButton,
  List,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";

import About from "./About";
import Backstage from "./Backstage";
import Dashboard from "./Dashboard";
import KeyboardShortcuts from "./KeyboardShortcuts";
import Logs from "./Logs";
import NavigationItem from "./NavigationItem";
import Personalization from "./Personalization";
import Privacy from "./Privacy";
import Profile from "./Profile";

export default function SystemSettings() {
  const theme = useTheme();
  const dispatch = useDispatch();
  const matchedMdUp = useMediaQuery(theme.breakpoints.up("md"));

  const auth = useSelector((state) => state.auth);
  const hasPbx = useSelector((state) => state.domain.pbx.context);
  const settingsState = useSelector((state) => state.app.systemSettingsPage);

  return (
    <Dialog
      open={!!settingsState}
      onClose={() => dispatch(setSystemSettingsPage(false))}
      aria-labelledby="system-settings-dialog"
      maxWidth="lg"
      slotProps={{
        paper: {
          id: "settings",
          elevation: 1,
          sx: {
            p: 0,
            display: "flex",
            flexFlow: "row nowrap",
            flexGrow: 1,
            maxHeight: { sx: "100%", md: 800 },
            height: { sx: 1, md: "calc(100% - 64px)" },
            m: { sx: 0, md: 4 },
            contain: "paint",
            [theme.breakpoints.down("md")]: {
              borderRadius: 0,
              height: "100%",
              maxHeight: "100%",
              margin: 0,
            },
          },
        },
      }}
      fullScreen={!matchedMdUp}
    >
      <Box
        sx={(theme) => ({
          width: { xs: 56, md: 260 },
          flexShrink: 0,
          maxHeight: "100%",
          display: "flex",
          flexFlow: "column nowrap",
          borderRight: "solid 1px",
          borderColor: theme.vars.palette.divider,
          height: 1,
          overflowY: "auto",
          position: "relative",
        })}
      >
        <AppBar
          sx={(theme) => ({
            backgroundImage: "none",
            backgroundColor: `color-mix(in srgb, ${theme.vars.palette.background.default} 75%, transparent)`,
            backdropFilter: "blur(15px) saturate(125%)",
            position: "sticky",
            top: 0,
            color: "inherit",
            border: "none",
            zIndex: 1200,
          })}
        >
          <Stack
            direction="row"
            sx={{
              alignItems: "center",
              gap: 0,
              m: 1,
            }}
          >
            <IconButton
              sx={{ height: 40, width: 40 }}
              color="inherit"
              onClick={() => dispatch(setSystemSettingsPage(false))}
            >
              <Valkyrie icon={viArrowLeft} />
            </IconButton>
            {matchedMdUp && (
              <Typography variant="h4" sx={{ m: 0, fontSize: 22 }}>
                Settings
              </Typography>
            )}
          </Stack>
        </AppBar>
        <List
          id="nav-settings"
          sx={{
            flexGrow: 1,
            display: "flex",
            flexFlow: "column nowrap",
            px: 1,
            pb: 1,
            ".MuiListItem-root": {
              width: 1,
              minHeight: 40,
            },
            ".MuiListItemButton-root": {
              width: 1,
              minHeight: 40,
              py: 0,
              pr: 1.5,
              pl: 1,
            },
            ".MuiListItemText-primary": {
              fontWeight: "600",
            },
          }}
        >
          <NavigationItem
            title={`${auth?.firstname} ${auth?.lastname ?? ""}`}
            secondary={auth.email}
            avatar={<Avatar text={`${auth?.firstname} ${auth?.lastname ?? ""}`} px={24} />}
            selected={settingsState === "profile"}
            onClick={() => dispatch(setSystemSettingsPage("profile"))}
            id="profileSettingsItem"
          />
          <Divider
            sx={{
              borderColor: (theme) => theme.vars.palette.divider,
              mx: -1,
              my: 0.75,
            }}
          />
          <NavigationItem
            title="Backstage"
            icon={viCity}
            selected={settingsState === "backstage"}
            onClick={() => dispatch(setSystemSettingsPage("backstage"))}
            id="backstageSettingsItem"
          />
          {hasPbx && (
            <NavigationItem
              title="Dashboard"
              icon={viGauge}
              selected={settingsState === "dashboard"}
              onClick={() => dispatch(setSystemSettingsPage("dashboard"))}
              id="dashboardSettingsItem"
            />
          )}
          <NavigationItem
            title="Logs & statistics"
            icon={viChartPie}
            selected={settingsState === "logs"}
            onClick={() => dispatch(setSystemSettingsPage("logs"))}
            id="logsSettingsItem"
          />
          <Divider
            sx={{
              borderColor: (theme) => theme.vars.palette.divider,
              mx: -1,
              my: 0.75,
            }}
          />
          <NavigationItem
            title="Personalization"
            icon={viSwatchbook}
            selected={settingsState === "personalization"}
            onClick={() => dispatch(setSystemSettingsPage("personalization"))}
            id="personalizationSettingsItem"
          />
          <NavigationItem
            title="Privacy"
            icon={viLock}
            selected={settingsState === "privacy"}
            onClick={() => dispatch(setSystemSettingsPage("privacy"))}
            id="privacySettingsItem"
          />
          <Divider
            sx={{
              borderColor: (theme) => theme.vars.palette.divider,
              mx: -1,
              my: 0.75,
            }}
          />
          <NavigationItem
            title="Keyboard shortcuts"
            icon={viKeyboard}
            selected={settingsState === "keyboard"}
            onClick={() => dispatch(setSystemSettingsPage("keyboard"))}
            id="keyboardSettingsItem"
          />
          <Box sx={{ flexGrow: 1 }} />
          <NavigationItem
            title={`About My Sippy ${getVersion("main")}`}
            icon={viSippy}
            selected={settingsState === "about"}
            onClick={() => dispatch(setSystemSettingsPage("about"))}
            id="aboutSettingsItem"
          />
        </List>
      </Box>
      <Box
        sx={{
          flexGrow: 1,
          flexFlow: "column nowrap",
          maxHeight: "100%",
          overflowY: "auto",
          position: "relative",
          p: 2,
        }}
      >
        {settingsState === "profile" && <Profile />}
        {settingsState === "backstage" && <Backstage />}
        {hasPbx && settingsState === "dashboard" && <Dashboard />}
        {settingsState === "logs" && <Logs />}
        {settingsState === "personalization" && <Personalization />}
        {settingsState === "privacy" && <Privacy />}
        {settingsState === "keyboard" && <KeyboardShortcuts />}
        {settingsState === "about" && <About />}
      </Box>
    </Dialog>
  );
}
